import { Button, Grid, Input, Text } from '@ltvco/refresh-lib/theme';
import { enqueueSnackbar } from '@ltvco/refresh-lib/v1';
import {
  UseMutationResult,
  useQueryClient,
  useForm,
  yupResolver,
  Yup,
} from '@ltvco/refresh-lib/vendors';
import { useRef, useState, useEffect, ChangeEvent, KeyboardEvent } from 'react';

interface ListParams {
  name: string;
  listCode?: string;
  permalink: string;
}

interface FormValues {
  listName: string;
}

interface ListFormProps {
  closeForm: () => void;
  title: string;
  mainActionButtonText: string;
  placeholderText: string;
  listCode?: string;
  action: UseMutationResult<unknown, unknown, ListParams, unknown>;
  currentListNames: string[];
  originalListName?: string;
}

const listNameLimit = 100;

const schema = Yup.object().shape({
  listName: Yup.string()
    .required('List name is required')
    .max(listNameLimit, `Must be at most ${listNameLimit} characters`),
});

export const ListForm = ({
  closeForm,
  title,
  mainActionButtonText,
  placeholderText,
  listCode,
  action,
  currentListNames,
  originalListName,
}: ListFormProps) => {
  const queryClient = useQueryClient();
  const [listNameCurrentLength, setListNameCurrentLength] = useState(0);
  const [loadingState, setLoadingState] = useState(false);
  const listInput = useRef<HTMLInputElement | null>(null);

  const {
    handleSubmit,
    register,
    clearErrors,
    formState: { errors },
    reset,
    setError,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      listName: '',
    },
  });

  const onSubmit = async ({ listName }: FormValues) => {
    const listNameTrimmed = listName.trim().toLocaleLowerCase();
    const originalTrimmed = originalListName?.trim().toLocaleLowerCase();
    const namesForDuplicateCheck =
      originalTrimmed !== undefined && originalTrimmed !== ''
        ? currentListNames.filter((name) => name !== originalTrimmed)
        : currentListNames;

    if (namesForDuplicateCheck.includes(listNameTrimmed)) {
      setError('listName', { message: 'List name already exists' });
      return;
    }

    const params = {
      name: listNameTrimmed,
      listCode,
      permalink: '',
    };

    setLoadingState(true);
    action.mutate(params, {
      onSuccess: () => {
        if (!listCode) {
          queryClient.invalidateQueries(['lists']);
        }
        enqueueSnackbar(
          listCode ? 'List updated successfully' : 'List created successfully',
          { variant: 'success' }
        );
      },
      onError: () => {
        enqueueSnackbar(
          listCode ? 'Failed to update list' : 'Failed to create list',
          { variant: 'error' }
        );
      },
      onSettled: () => {
        setLoadingState(false);
      },
    });
  };

  const handleCancelClick = () => {
    clearErrors('listName');
    closeForm();
    reset();
  };

  useEffect(() => {
    if (action.isSuccess) {
      clearErrors('listName');
      setListNameCurrentLength(0);
      closeForm();
      action.reset();
    }
  }, [action.isSuccess]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const { value } = event.target as HTMLInputElement;
      setValue('listName', value);
      handleSubmit(onSubmit)();
    }
  };

  const { onChange: registerListNameOnChange, ...listNameRegisterRest } =
    register('listName');

  return (
    <form
      onClick={(event) => event.stopPropagation()}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid
        container
        columnSpacing={2}
        sx={{ width: '287px', padding: (theme) => theme.spacing(2) }}
      >
        <Grid size={12}>
          <Text variant="subtitle1" color="primary">
            {title}
          </Text>
        </Grid>
        <Grid size={12}>
          <Input
            {...listNameRegisterRest}
            type="text"
            placeholder={placeholderText}
            inputRef={listInput}
            slotProps={{
              htmlInput: { maxLength: 100, onKeyDown: handleKeyDown },
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              registerListNameOnChange(e);
              setListNameCurrentLength(e.target.value.length);
            }}
            fullWidth
            sx={{ marginRight: (theme) => theme.spacing(1) }}
          />
        </Grid>
        <Grid size={12} textAlign="end">
          <Text variant="subtitle2" color="primary">
            {listNameCurrentLength}/{listNameLimit}
          </Text>
        </Grid>
        <Grid size={12} textAlign="start">
          <Text variant="subtitle2" color="error">
            {errors.listName?.message}
          </Text>
        </Grid>
        <Grid size={12}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ flex: '0 0 auto' }}
            disabled={loadingState}
          >
            {mainActionButtonText}
          </Button>
        </Grid>
        <Grid size={12}>
          <Button
            onClick={handleCancelClick}
            fullWidth
            sx={{ flex: '0 0 auto', textDecoration: 'underline' }}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
